import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSWRConfig } from "swr";

import {
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Drawer,
  Fab,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";
import * as Sentry from "@sentry/react";

import {
  UserMessage,
  AssistantMessage,
  ThinkingIndicator
} from "./ScoutMessages";
import { ScoutCardContext } from "./ScoutCardContext";
import { fabOpeners, SURFACE_TO_SCOPE } from "./fabOpeners";

import ScoutMark from "../../atoms/icons/ScoutMark";
import { useSupportSlider } from "../../atoms/buttons/TopBarSupportIcon";
import CommunicationCenter from "../../modules/CommunicationCenter";

import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import { getAccountId } from "../../../helpers/accountHelpers";
import { generateTempId } from "../../../helpers/otherHelpers";
import { analyticsTrack } from "../../../helpers/analytics";
import {
  useCachedDisplayData,
  useCachedFirebaseCrud
} from "../../../helpers/firebase";

const DESKTOP_PANEL_WIDTH = 420;
const DESKTOP_PANEL_HEIGHT = "min(70vh, 640px)";

const turnsFromConversation = (conversation) =>
  (conversation?.turns || []).map((turn) => ({
    role: turn.role,
    content: turn.content,
    sources: turn.sources || [],
    cards: turn.cards || [],
    _seq: turn._seq
  }));

const findScopedConversation = (conversations, scope) =>
  (conversations || []).find((conv) => conv.scope === scope && !conv.archived);

const ScoutFab = ({ surface, surfaceContext, defaultOpen = false }) => {
  const theme = useTheme();
  const { palette } = theme;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { openSupport, supportSliderJsx } = useSupportSlider();

  const scope = SURFACE_TO_SCOPE[surface];
  const chipList = fabOpeners[surface] || [];

  const [conversations, conversationsLoading] = useCachedDisplayData(
    "agent-conversations"
  );
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const { mutate } = useSWRConfig();

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [draftMessage, setDraftMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isExpertMeetingOpen, setIsExpertMeetingOpen] = useState(false);

  const bottomRef = useRef(null);
  const replyInputRef = useRef(null);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    if (surfaceContext) return;
    console.warn(
      `ScoutFab: surface "${surface}" mounted without surfaceContext — Scout will answer generically.`
    );
  }, [surface, surfaceContext]);

  useEffect(() => {
    if (conversationsLoading) return;
    if (conversationId) return;
    const existing = findScopedConversation(conversations, scope);
    if (existing) {
      setConversationId(existing.id);
      return;
    }
    setConversationId(String(generateTempId()));
  }, [conversations, conversationsLoading, conversationId, scope]);

  useEffect(() => {
    if (!conversationId) return;
    if (isSending) return;
    const conv = (conversations || []).find((c) => c.id === conversationId);
    if (!conv) return;
    const persisted = turnsFromConversation(conv);
    setMessages(persisted);
  }, [conversationId, conversations, isSending]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isSending]);

  useEffect(() => {
    if (!isSending && isOpen && messages.length > 0) {
      replyInputRef.current?.focus();
    }
  }, [isSending, isOpen, messages.length]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    analyticsTrack("scout_fab_opened", { surface, scope });
  }, [surface, scope]);

  const handleClose = useCallback(
    (reason) => {
      setIsOpen(false);
      analyticsTrack("scout_fab_closed", { surface, scope, reason });
    },
    [surface, scope]
  );

  const handleStartFresh = useCallback(() => {
    if (!conversationId) return;
    const isPersisted = (conversations || []).some(
      (c) => c.id === conversationId
    );
    if (isPersisted) {
      updateCollectionDoc("agent-conversations", conversationId, {
        archived: true
      });
    }
    setConversationId(String(generateTempId()));
    setMessages([]);
    setDraftMessage("");
    analyticsTrack("scout_fab_started_fresh", { surface, scope });
  }, [conversationId, conversations, updateCollectionDoc, surface, scope]);

  const openExpertMeeting = useCallback(() => setIsExpertMeetingOpen(true), []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        handleClose("escape");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, handleClose]);

  const cardContextValue = useMemo(
    () => ({ surface, openSupport, openExpertMeeting }),
    [surface, openSupport, openExpertMeeting]
  );

  const showChips = messages.length === 0 && !isSending && chipList.length > 0;

  const sendChat = (messageText) =>
    new Promise((resolve, reject) => {
      const data = {
        companyId: getAccountId(),
        conversationId,
        message: messageText,
        surface,
        scope,
        ...(surfaceContext ? { surfaceContext } : {})
      };
      fetchOurApi({
        path: "/agents/chat",
        method: "POST",
        data,
        callback: resolve
      }).catch(reject);
    });

  const sendMessage = async (messageText) => {
    const trimmed = (messageText || "").trim();
    if (!trimmed || isSending || !conversationId) return;

    const optimisticUserMessage = {
      role: "user",
      content: trimmed,
      _localId: String(generateTempId())
    };
    setMessages((prev) => [...prev, optimisticUserMessage]);
    setDraftMessage("");
    setIsSending(true);

    const appendErrorReply = (text) =>
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: text,
          sources: [],
          cards: [],
          isError: true,
          _localId: String(generateTempId())
        }
      ]);

    try {
      const first = await sendChat(trimmed);
      const needsRetry = first?.code === "turn_seq_conflict";
      const final = needsRetry ? await sendChat(trimmed) : first;

      const isConflict = final?.code === "turn_seq_conflict";
      const isScopeMismatch = final?.code === "scope_mismatch";
      const isError = !isConflict && !isScopeMismatch && final?.error === true;

      if (isConflict) {
        appendErrorReply("Scout had trouble sending that — try again.");
        Sentry.captureMessage("scout_fab persistent turn_seq_conflict", {
          level: "warning",
          tags: { feature: "scout-fab", surface, scope }
        });
      }
      if (isScopeMismatch) {
        appendErrorReply("Conversation scope conflict — try again.");
        Sentry.captureMessage("scout_fab scope_mismatch", {
          level: "warning",
          tags: { feature: "scout-fab", surface, scope }
        });
      }
      if (isError) {
        appendErrorReply(
          final.response ||
            "Something went wrong processing your request. Please try again."
        );
        Sentry.captureMessage("scout_fab backend error response", {
          level: "error",
          tags: { feature: "scout-fab", surface, scope },
          extra: { response: final?.response }
        });
      }
      if (!isConflict && !isScopeMismatch && !isError) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: final.response || "No response received.",
            sources: final.sources || [],
            cards: final.cards || [],
            _localId: String(generateTempId())
          }
        ]);
        await mutate("agent-conversations");
        analyticsTrack("scout_fab_message_sent", { surface, scope });
      }
    } catch (err) {
      appendErrorReply(
        "Something went wrong processing your request. Please try again."
      );
      Sentry.captureException(err, {
        tags: { feature: "scout-fab", surface, scope }
      });
    }

    setIsSending(false);
  };

  const handleChipClick = (chipText) => {
    if (isSending) return;
    analyticsTrack("scout_fab_chip_clicked", { surface, scope, chipText });
    sendMessage(chipText);
  };

  const handleInputKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (e.shiftKey) return;
    e.preventDefault();
    sendMessage(draftMessage);
  };

  const handleSendClick = () => sendMessage(draftMessage);

  const canStartFresh = messages.length > 0;

  const renderHeader = () => (
    <Box
      px={2}
      py={1}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        bgcolor: palette.primary.main,
        color: palette.common.white
      }}
    >
      <Typography variant="h6" color="inherit">
        Scout
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5}>
        {canStartFresh && (
          <Tooltip title="Start a fresh conversation">
            <IconButton
              size="small"
              onClick={handleStartFresh}
              aria-label="Start a fresh Scout conversation"
              sx={{ color: palette.common.white }}
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          size="small"
          onClick={() => handleClose("close-button")}
          aria-label="Close Scout"
          sx={{ color: palette.common.white }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );

  const renderChips = () => (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography variant="body2" color="textSecondary">
        Ask Scout about…
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={1}>
        {chipList.map((chipText) => (
          <Chip
            key={`fab-chip-${chipText}`}
            label={chipText}
            variant="outlined"
            color="primary"
            size="small"
            clickable
            onClick={() => handleChipClick(chipText)}
          />
        ))}
      </Box>
    </Box>
  );

  const messageKey = (msg, idx) => {
    if (msg._localId) return `fab-msg-local-${msg._localId}`;
    if (msg._seq !== undefined) return `fab-msg-seq-${msg._seq}`;
    return `fab-msg-idx-${idx}`;
  };

  const renderMessages = () =>
    messages.map((msg, idx) =>
      msg.role === "user" ? (
        <UserMessage key={messageKey(msg, idx)} content={msg.content} />
      ) : (
        <AssistantMessage
          key={messageKey(msg, idx)}
          content={msg.content}
          cards={msg.cards}
        />
      )
    );

  const renderBody = () => (
    <Box
      flexGrow={1}
      display="flex"
      flexDirection="column"
      gap={2}
      px={2}
      py={2}
      sx={{
        overflowY: "auto",
        overflowX: "hidden",
        minHeight: 0,
        bgcolor: palette.background.paper
      }}
    >
      {showChips && renderChips()}
      {renderMessages()}
      {isSending && <ThinkingIndicator />}
      <div ref={bottomRef} />
    </Box>
  );

  const renderInput = () => (
    <Box
      display="flex"
      alignItems="flex-end"
      gap={1}
      px={2}
      py={1}
      sx={{
        bgcolor: palette.background.paper,
        borderTop: `1px solid ${palette.backgroundGray.main}`
      }}
    >
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Ask Scout anything"
        value={draftMessage}
        onChange={(e) => setDraftMessage(e.target.value)}
        onKeyDown={handleInputKeyDown}
        multiline
        maxRows={4}
        disabled={isSending}
        inputRef={replyInputRef}
      />
      <IconButton
        color="primary"
        onClick={handleSendClick}
        disabled={isSending || !draftMessage.trim()}
        aria-label="Send message"
      >
        {isSending ? <CircularProgress size={20} /> : <SendIcon />}
      </IconButton>
    </Box>
  );

  const renderDesktopPanel = () => (
    <ClickAwayListener onClickAway={() => handleClose("outside-click")}>
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          bottom: theme.spacing(3),
          right: theme.spacing(3),
          width: DESKTOP_PANEL_WIDTH,
          height: DESKTOP_PANEL_HEIGHT,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: theme.zIndex.appBar
        }}
      >
        {renderHeader()}
        {renderBody()}
        {renderInput()}
      </Paper>
    </ClickAwayListener>
  );

  const renderMobileDrawer = () => (
    <Drawer
      anchor="bottom"
      open={isOpen}
      onClose={() => handleClose("drawer-backdrop")}
      sx={{ zIndex: theme.zIndex.appBar }}
      PaperProps={{
        sx: {
          height: "100vh",
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      {renderHeader()}
      {renderBody()}
      {renderInput()}
    </Drawer>
  );

  return (
    <ScoutCardContext.Provider value={cardContextValue}>
      {supportSliderJsx}
      {isExpertMeetingOpen && (
        <CommunicationCenter
          initialTab="sales"
          isSlideOpen={isExpertMeetingOpen}
          setIsSlideOpen={setIsExpertMeetingOpen}
        />
      )}
      {!isOpen && (
        <Fab
          color="primary"
          onClick={handleOpen}
          aria-label="Ask Scout"
          variant={isMobile ? "circular" : "extended"}
          sx={{
            position: "fixed",
            bottom: theme.spacing(3),
            right: theme.spacing(3),
            zIndex: theme.zIndex.fab,
            color: palette.common.white
          }}
        >
          <ScoutMark size={24} color={palette.common.white} />
          {!isMobile && (
            <Box ml={1} color={palette.common.white}>
              <Typography variant="button" color="inherit">
                Ask Scout
              </Typography>
            </Box>
          )}
        </Fab>
      )}
      {isOpen && (isMobile ? renderMobileDrawer() : renderDesktopPanel())}
    </ScoutCardContext.Provider>
  );
};

export default ScoutFab;
